import { type DocumentNode, useLazyQuery, useMutation } from "@apollo/client";
import { stripTypename } from "@apollo/client/utilities";
import { useToaster } from "@package/ui-components";
import {
  CreateLocationDocument,
  GetLocationDocument,
} from "@package/ui-gql/index-data-access";
import { useState } from "react";

export type UseDuplicateItemArgs = {
  title: string;
  getDocument?: DocumentNode;
  createDocument?: DocumentNode;
  nameField?: string;
  refetchQueries?: DocumentNode[];
  onDuplicated?: (id?: string) => void;
};

export function useDuplicateItem({
  title,
  getDocument,
  createDocument,
  nameField = "name",
  refetchQueries,
  onDuplicated,
}: UseDuplicateItemArgs) {
  const { toaster } = useToaster();
  const [isDuplicateInflight, setIsDuplicateInflight] =
    useState<boolean>(false);
  const isConfigured = !!getDocument && !!createDocument;

  // useLazyQuery/useMutation must be called unconditionally on every render,
  // so an unconfigured caller still needs a valid document here -- gated
  // behind `isConfigured` below so it's never actually queried/mutated.
  const [fetchItemToDuplicate] = useLazyQuery(
    getDocument ?? GetLocationDocument,
  );
  const [createDuplicateItem] = useMutation(
    createDocument ?? CreateLocationDocument,
  );

  const duplicateItem = async (id?: string) => {
    if (!id) {
      return;
    }

    if (!isConfigured) {
      console.error(
        `useDuplicateItem: duplicate requested for "${title}" without both getDocument and createDocument configured.`,
      );
      return;
    }

    setIsDuplicateInflight(true);
    const toastId = toaster?.show({
      icon: "duplicate",
      intent: "none",
      message: `Duplicating ${title}`,
      timeout: 10000,
    });

    const { data } = await fetchItemToDuplicate({
      fetchPolicy: "network-only",
      variables: { id },
    });
    const item = (data as { item?: Record<string, unknown> } | undefined)?.item;

    if (!item) {
      setIsDuplicateInflight(false);
      if (toastId) {
        toaster?.dismiss(toastId);
      }
      toaster?.show({
        icon: "error",
        intent: "danger",
        message: `Failed duplicating ${title}`,
        timeout: 5000,
      });
      return;
    }

    const variables = {
      ...stripTypename(item),
      [nameField]: `${item[nameField]} (Copy)`,
    };

    await createDuplicateItem({
      refetchQueries,
      variables,
      onCompleted: (result: { data?: { id?: string } }) => {
        onDuplicated?.(result?.data?.id);

        if (toastId) {
          toaster?.dismiss(toastId);
        }
        toaster?.show({
          icon: "tick",
          intent: "success",
          message: `Duplicated ${title}`,
          timeout: 3000,
        });
      },
      onError: () => {
        if (toastId) {
          toaster?.dismiss(toastId);
        }
        toaster?.show({
          icon: "error",
          intent: "danger",
          message: `Failed duplicating ${title}`,
          timeout: 5000,
        });
      },
    });

    setIsDuplicateInflight(false);
  };

  return { duplicateItem, isDuplicateInflight, isConfigured };
}
